import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// Wrap React.lazy so a failed dynamic import auto-recovers.
//
// After a deploy, a returning user's stale tab may request a code-split chunk
// that no longer exists. The SPA 404-fallback returns index.html, so the browser
// rejects the module with "'text/html' is not a valid JavaScript MIME type"
// (surfaced as a TypeError) and the route crashes to the error boundary.
//
// Instead, reload the page once to pull the fresh index.html + current chunks.
// A sessionStorage guard prevents an infinite reload loop if the failure is real.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const key = 'chunk-reload:' + (factory.toString().match(/import\(\s*["'`](.+?)["'`]\s*\)/)?.[1] ?? factory.toString().slice(0, 48));
      let alreadyTried: string | null = null;
      try { alreadyTried = sessionStorage.getItem(key); } catch { /* storage blocked */ }
      if (!alreadyTried) {
        try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ }
        window.location.reload();
        // Hold the render until the reload takes over.
        return new Promise<{ default: T }>(() => {});
      }
      throw err; // already retried once — let the error boundary show the fallback
    }
  });
}
